import React, { useEffect, useState } from 'react';
import { Medal as MedalIcon } from 'lucide-react';
import {
  useGetAllCoursesQuery,
  useGetCourseDetailedReportQuery,
} from '../redux/apiSlice';

interface Section {
  name: string;
  accuracy: number;
  aiUsage: number;
}

interface Course {
  id: string;
  name: string;
}

const PersonalizedFeedback: React.FC = () => {
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const { data: courses, isLoading, isError } = useGetAllCoursesQuery();
  const [completedCourses, setCompletedCourses] = useState<Course[]>([]);
  const {
    data: courseReport,
    isLoading: isReportLoading,
    isError: isReportError,
    refetch: refetchReport,
  } = useGetCourseDetailedReportQuery(
    selectedCourse ? parseInt(selectedCourse) : 0,
    { skip: !selectedCourse },
  );

  useEffect(() => {
    if (courses) {
      setCompletedCourses(
        courses.courses
          .filter((course) => course?.detail?.course_completed)
          .map((course) => ({
            id: course.id.toString(),
            name: course.course_name,
          })),
      );
    }
  }, [courses]);

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourse(e.target.value);
  };

  const getAIFeedback = (courseId: string) => {
    if (!courseId || !courseReport) {
      return 'Select a course to view personalized feedback.';
    }

    const sections: Section[] = [];

    // Transform API data into sections format
    Object.keys(courseReport.error_rate).forEach((sectionId) => {
      if (sectionId !== 'overall_rate') {
        const errorData = courseReport.error_rate[sectionId];
        const aiData = courseReport.ai_usage_rate[sectionId];

        if (errorData && aiData) {
          sections.push({
            name: errorData.name,
            accuracy: 100 - errorData.error_rate, // Convert error rate to accuracy
            aiUsage: aiData.ai_usage_rate,
          });
        }
      }
    });

    let strengths = '';
    let areasForImprovement = '';

    // Find the section with the highest accuracy (strength)
    if (sections.length > 0) {
      const highestAccuracySection = [...sections].sort(
        (a, b) => b.accuracy - a.accuracy,
      )[0];

      // Find sections with accuracy below 75% (areas for improvement)
      const improvementSections = sections.filter(
        (section: Section) => section.accuracy < 75,
      );

      strengths = `Your strength is in ${highestAccuracySection.name} with an impressive accuracy score of ${highestAccuracySection.accuracy.toFixed(1)}%.`;

      if (improvementSections.length > 0) {
        areasForImprovement =
          'You may benefit from additional practice in: ' +
          improvementSections
            .map(
              (section: Section) =>
                `${section.name} (${section.accuracy.toFixed(1)}% accuracy)`,
            )
            .join(', ') +
          '. Consider revisiting to strengthen your understanding.';
      } else {
        areasForImprovement =
          "You've shown good understanding across all sections! To further enhance your skills, try applying these concepts to more complex projects.";
      }
    } else {
      return 'No section data available for this course yet.';
    }

    return `${strengths} ${areasForImprovement}`;
  };

  const renderMedalIcon = (medal: string, size = 24) => {
    switch (medal) {
      case 'gold':
        return <MedalIcon color="gold" size={size} />;
      case 'silver':
        return <MedalIcon color="silver" size={size} />;
      case 'bronze':
      default:
        return <MedalIcon color="brown" size={size} />;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading your courses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Failed to Load Courses
          </h3>
          <p className="text-red-700 mb-4">
            We couldn't load your courses. Please check your connection and try
            again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Transform the API data into sections for the table
  const getSections = (): Section[] => {
    if (!courseReport) return [];

    const sections: Section[] = [];

    Object.keys(courseReport.error_rate).forEach((sectionId) => {
      if (sectionId !== 'overall_rate') {
        const errorData = courseReport.error_rate[sectionId];
        const aiData = courseReport.ai_usage_rate[sectionId];

        if (errorData && aiData) {
          sections.push({
            name: errorData.name,
            accuracy: 100 - errorData.error_rate * 100, // Convert error rate to accuracy
            aiUsage: aiData.ai_usage_rate * 100,
          });
        }
      }
    });

    return sections;
  };

  return (
    <div className="container mx-auto p-6">
      <div className="my-6">
        <label className="font-medium text-gray-700 text-lg">
          Course Filter:{' '}
        </label>
        <select
          className="ml-2 p-2 border rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={selectedCourse}
          onChange={handleCourseChange}
        >
          <option value="">My Courses</option>
          {completedCourses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedCourse ? (
        <div className="bg-yellow-50 border border-yellow-100 rounded-md p-6 text-center text-gray-700">
          <p className="text-md">
            Personalized feedback reports will be available after you
            successfully complete your first course. Keep coding and check back
            soon for detailed insights on your progress and areas for
            improvement!
          </p>
        </div>
      ) : isReportLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading course report...</p>
          </div>
        </div>
      ) : isReportError ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Failed to Load Course Report
          </h3>
          <p className="text-red-700 mb-4">
            We couldn't load the detailed report for this course. Please try
            again.
          </p>
          <button
            onClick={() => refetchReport()}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-md shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-800 border-b pb-2">
              1. Skill Strengths and Areas for Improvement:
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {courseReport?.personalized_feedback ||
                getAIFeedback(selectedCourse)}
            </p>
          </div>

          <div className="bg-white rounded-md shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-800 border-b pb-2">
              2. My Code Dragon AI Usage:
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 text-left">Section</th>
                    <th className="py-3 px-4 text-center">Accuracy Score</th>
                    <th className="py-3 px-4 text-center">AI Usage (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {getSections().map((section: Section, index: number) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{section.name}</td>
                      <td className="py-3 px-4 text-center">
                        {section.accuracy || section.accuracy === 0
                          ? section.accuracy.toFixed(1)
                          : 'N/A'}
                        %
                      </td>
                      <td className="py-3 px-4 text-center">
                        {section.aiUsage || section.aiUsage === 0
                          ? section.aiUsage.toFixed(1)
                          : 'N/A'}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-md shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-800 border-b pb-2">
              3. Overall Course Error Rate and AI Usage:
            </h2>
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0">
                <p className="font-medium">
                  Error Rate:{' '}
                  <span className="text-red-600">
                    {courseReport?.error_rate.overall_rate ||
                    courseReport?.error_rate.overall_rate === 0
                      ? (courseReport?.error_rate.overall_rate * 100).toFixed(1)
                      : 'N/A'}
                    %
                  </span>
                </p>
                <p className="font-medium">
                  AI Assistance Usage:{' '}
                  <span className="text-blue-600">
                    {courseReport?.ai_usage_rate.overall_ai_rate ||
                    courseReport?.ai_usage_rate.overall_ai_rate === 0
                      ? (
                          courseReport?.ai_usage_rate.overall_ai_rate * 100
                        ).toFixed(1)
                      : 'N/A'}
                    %
                  </span>
                </p>
              </div>
              <div className="flex items-center">
                <p className="mr-3 font-medium">Medal Earned:</p>
                {courseReport && renderMedalIcon(courseReport.medal, 36)}
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <h3 className="font-semibold mb-2">
                Dragon Mastery Medals Explained:
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li className="flex items-center">
                  <span className="mr-2">{renderMedalIcon('gold', 20)}</span>
                  Gold Medal: Awarded for completing a course with high accuracy
                  and mostly independent. (With an error rate &lt; 6% and AI
                  assistance usage &lt;= 25%)
                </li>
                <li className="flex items-center">
                  <span className="mr-2">{renderMedalIcon('silver', 20)}</span>
                  Silver Medal: Finished a course with a few more mistakes and
                  hints along the way. (An error rate of 6%-20% and AI
                  assistance usage &lt;= 25%)
                </li>
                <li className="flex items-center">
                  <span className="mr-2">{renderMedalIcon('bronze', 20)}</span>
                  Bronze Medal: Completed a course regardless of accuracy or AI
                  help used. (Every finish deserves celebration!)
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PersonalizedFeedback;
